import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FiArrowRight, FiBookOpen, FiFileText } from "react-icons/fi";

import {
  getLastThreePublications,
  getPublications,
} from "../services/publicationsServices";
import { toAbsolutePublicUrl } from "../services/mediaLibraryServices";
import Reveal from "./ui/Reveal";

const FALLBACK_PUBLICATION_IMAGE = "/images/publication-placeholder.jpg";
const PUBLICATIONS_PAGE_PATH = "/publications";

const stripHtml = (html = "") =>
  String(html || "")
    .replace(/<style[^>]*>.*?<\/style>/gis, "")
    .replace(/<script[^>]*>.*?<\/script>/gis, "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\s+/g, " ")
    .trim();

const makeExcerpt = (html = "", max = 170) => {
  const text = stripHtml(html);

  if (!text) return "";

  return text.length > max ? `${text.slice(0, max).trim()}…` : text;
};

const isAbsoluteUrl = (url = "") => /^https?:\/\//i.test(url);

const normalizeImageUrl = (url = "") => {
  if (!url) return FALLBACK_PUBLICATION_IMAGE;
  if (isAbsoluteUrl(url)) return url;

  return toAbsolutePublicUrl(url);
};

const getPublicationDate = (publication) =>
  publication?.date_publication ||
  publication?.published_at ||
  publication?.createdAt ||
  publication?.created_at ||
  null;

const formatDateLabel = (value) => {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("fr-FR", {
    month: "long",
    year: "numeric",
  }).format(date);
};

const normalizePublication = (publication = {}) => {
  const imageRaw =
    publication.image_publication_url ||
    publication.image_url ||
    publication.imageUrl ||
    publication.image ||
    "";

  const descriptionRaw =
    publication.description ||
    publication.contenu ||
    publication.content ||
    publication.description_publication ||
    "";

  const date = getPublicationDate(publication);

  return {
    ...publication,
    id: publication.id,
    title: publication.titre || publication.title || "Publication municipale",
    category:
      publication.categorie ||
      publication.category ||
      publication.type_publication ||
      "",
    image: normalizeImageUrl(imageRaw),
    descriptionRaw,
    descriptionPreview: makeExcerpt(descriptionRaw),
    dateLabel: formatDateLabel(date),
  };
};

function PublicationCard({ publication, delay = 0 }) {
  return (
    <Reveal delay={delay} className="h-full">
      <article className="group flex h-full flex-col overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:border-[#1277B9]/50 hover:shadow-2xl hover:shadow-slate-950/10 dark:border-slate-800 dark:bg-slate-950">
        <Link
          to={PUBLICATIONS_PAGE_PATH}
          className="relative block overflow-hidden bg-slate-100 text-left dark:bg-slate-900"
          aria-label={`Consulter la publication ${publication.title}`}
        >
          <img
            src={publication.image}
            alt={publication.title}
            loading="lazy"
            className="aspect-[3/4] w-full object-cover transition duration-700 group-hover:scale-105"
            onError={(event) => {
              event.currentTarget.src = FALLBACK_PUBLICATION_IMAGE;
            }}
          />

          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-slate-950/10 to-transparent" />

          <div className="absolute bottom-4 left-4 right-4 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/95 px-3 py-1 text-xs font-bold text-[#1277B9] shadow-sm backdrop-blur dark:bg-slate-950/90 dark:text-sky-300">
              <FiFileText className="h-3.5 w-3.5" />
              Publication
            </span>

            {publication.category && (
              <span className="inline-flex rounded-full bg-slate-950/75 px-3 py-1 text-xs font-bold text-white shadow-sm backdrop-blur">
                {publication.category}
              </span>
            )}
          </div>
        </Link>

        <div className="flex flex-1 flex-col p-5">
          {publication.dateLabel && (
            <p className="text-[10px] font-extralight uppercase tracking-[0.16em] text-slate-600 dark:text-slate-500">
              Publié en {publication.dateLabel}
            </p>
          )}

          <h3 className="mt-3 line-clamp-2 text-xl font-bold leading-snug text-slate-950 dark:text-white">
            {publication.title}
          </h3>

          <p className="mt-4 line-clamp-4 text-sm leading-7 text-slate-600 dark:text-slate-300">
            {publication.descriptionPreview ||
              "Consultez cette publication municipale mise à disposition par la mairie."}
          </p>

          <div className="mt-auto flex flex-wrap gap-2 pt-5">
            <Link
              to={PUBLICATIONS_PAGE_PATH}
              className="group/btn inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-2xl bg-[#1277B9] px-4 py-2 text-sm font-bold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-[#0f659d] focus:outline-none focus:ring-2 focus:ring-[#1277B9]/40 sm:flex-none"
            >
              Consulter
              <FiArrowRight className="h-4 w-4 transition group-hover/btn:translate-x-1" />
            </Link>
          </div>
        </div>
      </article>
    </Reveal>
  );
}

function PublicationSkeletonCard() {
  return (
    <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950">
      <div className="aspect-[3/4] animate-pulse bg-slate-200 dark:bg-slate-800" />

      <div className="space-y-4 p-5">
        <div className="h-4 w-32 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
        <div className="h-6 w-4/5 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />

        <div className="space-y-2">
          <div className="h-3 w-full animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
          <div className="h-3 w-5/6 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
          <div className="h-3 w-2/3 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
        </div>

        <div className="h-11 w-32 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800" />
      </div>
    </div>
  );
}

export default function PublicationsSection() {
  const [rawItems, setRawItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const fetchPublications = async () => {
      try {
        const data =
          (typeof getLastThreePublications === "function" &&
            (await getLastThreePublications())) ||
          (typeof getPublications === "function" && (await getPublications())) ||
          [];

        if (!mounted) return;

        const normalized = (Array.isArray(data) ? data : [])
          .filter((publication) => publication?.is_active !== false)
          .map(normalizePublication)
          .slice(0, 4);

        setRawItems(normalized);
      } catch (error) {
        console.error("Erreur chargement publications:", error);

        if (mounted) {
          setRawItems([]);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchPublications();

    return () => {
      mounted = false;
    };
  }, []);

  const items = useMemo(() => rawItems, [rawItems]);

  if (!loading && !items.length) return null;

  return (
    <section
      id="publications"
      className="relative overflow-hidden bg-slate-50 px-4 py-14 dark:bg-slate-950 sm:px-6 lg:px-8 lg:py-20"
      aria-labelledby="publications-home-title"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-24 top-16 h-72 w-72 rounded-full bg-sky-300/15 blur-3xl dark:bg-sky-500/10" />
        <div className="absolute -right-24 bottom-12 h-80 w-80 rounded-full bg-emerald-300/10 blur-3xl dark:bg-emerald-500/10" />
      </div>

      <div className="relative mx-auto max-w-[1380px]">
        <div className="mb-8 flex flex-col gap-5 md:mb-10 md:flex-row md:items-end md:justify-between">
          <Reveal>
            <div>
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200">
                <span className="h-2.5 w-2.5 rounded-full bg-[#1277B9]" />
                Publications municipales
              </div>

              <h2
                id="publications-home-title"
                className="text-3xl font-bold tracking-tight text-slate-950 dark:text-white md:text-4xl lg:text-5xl"
              >
                Publications
              </h2>

              <p className="mt-4 max-w-2xl text-base leading-7 text-slate-600 dark:text-slate-300">
                Consultez les bulletins, documents d’information et publications
                mis à disposition par la commune.
              </p>
            </div>
          </Reveal>

          <Reveal>
            <Link
              to={PUBLICATIONS_PAGE_PATH}
              className="group inline-flex min-h-12 items-center justify-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-800 shadow-sm transition hover:-translate-y-0.5 hover:border-[#1277B9]/40 hover:text-[#1277B9] dark:border-slate-800 dark:bg-slate-900 dark:text-white dark:hover:border-sky-500/40 dark:hover:text-sky-300"
            >
              Toutes les publications
              <FiArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {loading
            ? Array.from({ length: 4 }).map((_, index) => (
                <PublicationSkeletonCard key={index} />
              ))
            : items.map((publication, index) => (
                <PublicationCard
                  key={publication.id || `${publication.title}-${index}`}
                  publication={publication}
                  delay={index * 100}
                />
              ))}
        </div>

        {!loading && !items.length && (
          <div className="rounded-[2rem] border border-dashed border-slate-300 bg-white p-8 text-center shadow-sm dark:border-slate-700 dark:bg-slate-950">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl bg-[#1277B9]/10 text-[#1277B9] dark:bg-[#1277B9]/20 dark:text-sky-300">
              <FiBookOpen className="h-7 w-7" />
            </div>

            <h3 className="mt-5 text-xl font-bold text-slate-950 dark:text-white">
              Aucune publication disponible
            </h3>

            <p className="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">
              Les publications municipales seront affichées ici dès leur mise en
              ligne.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}